import React, { useEffect, useRef, useState } from 'react'
import { Alert, Col, Icon, Row, Spin } from 'antd'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import moment from 'moment'
import Auxiliary from '../../util/Auxiliary'
import Widget from '../../components/Widget'
import { fetchResource as getAssetDetails } from '../../appRedux/actions/Asset'
import axios from 'util/Api'
import BleBeaconInfo from './BleBeaconInfo'
import BleBeaconMap from './BleBeaconMap'
import { getBleTagAsset, getBeaconDeviceId, getLatestBeaconRecord, sortBeaconData } from './helpers'
import './style.css'

const mapStateToProps = ({ commonData, auth, asset }) => ({
  loading: commonData.loading,
  authUser: auth.authUser,
  asset: asset.edit
})

function mapDispatchToProps() {
  return {
    getAssetDetails
  }
}

function BleBeaconDetails(props) {
  const [assetId, setAssetId] = useState(null)
  const [mac, setMac] = useState(null)
  const [assetData, setAssetData] = useState(null)
  const [bleTagAsset, setBleTagAsset] = useState(null)
  const [latestRecord, setLatestRecord] = useState(null)
  const [detectorAsset, setDetectorAsset] = useState(null)
  const [detectorAssetsMap, setDetectorAssetsMap] = useState({})
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState(null)
  const [historyData, setHistoryData] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [beaconLoading, setBeaconLoading] = useState(false)
  const [isLiveFeedActive, setIsLiveFeedActive] = useState(false)
  const [liveTowingRecord, setLiveTowingRecord] = useState(null)
  const [lastTowDeviceAsset, setLastTowDeviceAsset] = useState(null)
  const liveFeedIntervalRef = useRef(null)
  const [mapMaximized, setMapMaximized] = useState(false)
  const [infoPanelExpanded, setInfoPanelExpanded] = useState(false)
  const [mapHeight, setMapHeight] = useState('calc(85vh - 24px)')

  useEffect(() => {
    setMapHeight(mapMaximized ? 'calc(100vh - 64px)' : 'calc(85vh - 24px)')
    if (!mapMaximized) setInfoPanelExpanded(false)
  }, [mapMaximized])
  const [error, setError] = useState(null)
  const [orgModules, setOrgModules] = useState([])

  useEffect(() => {
    const routeAssetId = props.match.params.asset
    const routeMac = props.match.params.mac
    if (routeAssetId) setAssetId(routeAssetId)
    if (routeMac) setMac(decodeURIComponent(routeMac))
  }, [])

  useEffect(() => {
    if (props.authUser) {
      setOrgModules(props.authUser && props.authUser.organisation && props.authUser.organisation.assetProModules || [])
    }
  }, [props.authUser])

  useEffect(() => {
    if (assetId) props.getAssetDetails(assetId)
  }, [assetId])

  useEffect(() => {
    if (props.asset && props.asset._id === assetId) {
      setAssetData(props.asset)
      setBleTagAsset(getBleTagAsset(props.asset, mac))
    }
  }, [props.asset, assetId, mac])

  const fetchBeaconData = async (from, to, shouldShowHistory) => {
    if (!mac) return
    setBeaconLoading(true)
    setError(null)
    const historyParams = { from, to, mac }
    try {
      const [lastRecordResponse, historyResponse] = await Promise.all([
        axios.get('/iot-device/beacon-last-record', { params: { mac } }),
        shouldShowHistory ? axios.get('/iot-device/beacon-data', { params: historyParams }) : Promise.resolve(null)
      ])
      const data = historyResponse ? sortBeaconData(historyResponse.data && historyResponse.data.data || []) : []
      const newDetectorAsset = (lastRecordResponse.data && lastRecordResponse.data.detectorAsset) || null
      setLatestRecord((lastRecordResponse.data && lastRecordResponse.data.data) || getLatestBeaconRecord(data))
      setDetectorAsset(newDetectorAsset)
      if (shouldShowHistory) {
        setHistoryData(data)
        setShowHistory(true)
        // build a map of detectorDeviceId → asset info for all unique detectors in history
        const baseMap = newDetectorAsset && newDetectorAsset.deviceId ? { [newDetectorAsset.deviceId]: newDetectorAsset } : {}
        const uniqueDeviceIds = [...new Set(data.map(r => getBeaconDeviceId(r)).filter(id => id && id !== '-'))]
        const missing = uniqueDeviceIds.filter(id => !baseMap[id])
        const results = await Promise.all(
          missing.map(id =>
            axios.get('/iot-device/detector-asset', { params: { deviceId: id } }).then(res => res.data).catch(() => null)
          )
        )
        missing.forEach((id, i) => { if (results[i]) baseMap[id] = results[i] })
        setDetectorAssetsMap(baseMap)
      }
    } catch (err) {
      setError(err && err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : 'Failed to fetch beacon data.')
    } finally {
      setBeaconLoading(false)
    }
  }

  const refreshLatest = () => {
    const to = moment().valueOf()
    const from = moment().subtract(48, 'hours').valueOf()
    fetchBeaconData(from, to, false)
  }

  const fetchLiveTowing = (instanceId) => {
    axios.get(`towing-events/live-beacon?instanceId=${encodeURIComponent(instanceId)}`)
      .then(res => {
        if (!res || !res.data) return
        const data = res.data
        setLiveTowingRecord(data)
        // Look up the asset for whichever device is relevant (active tow or last record)
        const deviceId = (data.towingRecord && data.towingRecord.deviceId)
          || (data.latestRecord && data.latestRecord.deviceId)
        if (deviceId) {
          axios.get(`iot-device/detector-asset?deviceId=${encodeURIComponent(deviceId)}`)
            .then(r => { if (r && r.data) setLastTowDeviceAsset(r.data) })
            .catch(() => {})
        } else {
          setLastTowDeviceAsset(null)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (mac) {
      refreshLatest()
      fetchLiveTowing(mac)
    }
  }, [mac])

  // Live feed: poll beacon status + towing status while active
  useEffect(() => {
    if (!isLiveFeedActive || !mac) {
      if (liveFeedIntervalRef.current) clearInterval(liveFeedIntervalRef.current)
      return
    }

    const pollLive = () => {
      const to = moment().valueOf()
      const from = moment().subtract(48, 'hours').valueOf()
      fetchBeaconData(from, to, false)
      fetchLiveTowing(mac)
    }

    pollLive()
    liveFeedIntervalRef.current = setInterval(pollLive, 10000)

    return () => { if (liveFeedIntervalRef.current) clearInterval(liveFeedIntervalRef.current) }
  }, [isLiveFeedActive, mac])

  const canUseTelematics = orgModules && orgModules.includes('Telematics')
  const isEligible = !!bleTagAsset

  return (
    <Auxiliary>
      <Spin spinning={props.loading || beaconLoading}>
        {!canUseTelematics ? (
          <Alert
            type="warning"
            showIcon
            message="Telematics module is not enabled for this organisation."
          />
        ) : (
          <>
            {error && (
              <Alert
                type="error"
                showIcon
                message={error}
                style={{ marginBottom: 12 }}
              />
            )}
            {assetData && !isEligible && (
              <Alert
                type="warning"
                showIcon
                message="This asset does not have an active dependent BLE tag with the requested MAC ID."
                style={{ marginBottom: 12 }}
              />
            )}

            <Row type="flex" align="top" className="ble-beacon-details-row" style={{ position: 'relative' }}>
              <Col
                sm={24} xs={24}
                style={mapMaximized
                  ? { flex: '0 0 100%', maxWidth: '100%' }
                  : { flex: '0 0 66.666667%', maxWidth: '66.666667%' }}
              >
                {!mapMaximized && (
                  <Row>
                    <Col sm={24} xs={24} lg={12}>
                      <Widget styleName="gx-card-full gx-dot-arrow-hover">
                        <div className="gx-media gx-align-items-center gx-flex-nowrap">
                          <div className="gx-px-3 gx-build-box-lay">
                            <img alt="Asset" src={require('assets/images/widget/fixed-asset.png')} />
                          </div>
                          <div className="gx-media-body gx-py-3 gx-pr-4 gx-build-box-lay-content">
                            <h2 className="h4 gx-text-truncate gx-mb-1">
                              {assetData ? (
                                <Link to={`/asset/history-report/${assetData._id}`}>
                                  {assetData.assetCode || '-'} ({assetData.altName || '-'}) {assetData.ownerCode || '-'}
                                </Link>
                              ) : '-'}
                            </h2>
                            <p className="gx-mb-1 gx-text-black gx-fs-sm gx-mt-1">
                              {assetData && assetData.assetModel && assetData.assetModel.name ? assetData.assetModel.name : '-'}
                            </p>
                            <p className="gx-mb-0 gx-text-grey gx-fs-xs gx-mt-1">
                              BLE Tag: {bleTagAsset && bleTagAsset.assetCode ? bleTagAsset.assetCode : '-'} ({mac || '-'})
                            </p>
                          </div>
                        </div>
                      </Widget>
                    </Col>
                    <Col sm={24} xs={24} lg={12}>
                      <Widget styleName="gx-card-full gx-dot-arrow-hover">
                        <div className="gx-media gx-align-items-center gx-flex-nowrap">
                          <div className="gx-px-3 gx-build-box-lay">
                            <img alt="Location" src={require('assets/images/widget/location.png')} />
                          </div>
                          <div className="gx-media-body gx-py-3 gx-pr-4 gx-build-box-lay-content">
                            <h2 className="h4 gx-text-truncate gx-mb-1">
                              {assetData && assetData.location && assetData.location.name ? assetData.location.name : '-'}
                            </h2>
                            <p className="gx-mb-1 gx-text-black gx-fs-md gx-mt-1">&nbsp;</p>
                            <p className="gx-mb-0 gx-text-grey gx-fs-md gx-mt-1">
                              {assetData && assetData.client && assetData.client.name ? assetData.client.name : '-'}
                            </p>
                          </div>
                        </div>
                      </Widget>
                    </Col>
                  </Row>
                )}

                <div className={`gx-card ble-beacon-map-card${mapMaximized ? ' ble-beacon-map-card-maximized' : ''}`} style={{ marginBottom: 0 }}>
                  <div className="gx-card-body ble-beacon-map-card-body" style={{ height: mapHeight, padding: 0, position: 'relative' }}>
                    {mapMaximized && assetData && (
                      <div className="device-map-float-info">
                        <div className="device-map-float-asset">
                          <strong>
                            <Link to={`/asset/history-report/${assetData._id}`} style={{ color: 'inherit' }}>
                              {assetData.assetCode || '-'} ({assetData.altName || '-'})
                            </Link>
                          </strong>
                          <span className="device-map-float-sub">
                            BLE Tag: {bleTagAsset && bleTagAsset.assetCode ? bleTagAsset.assetCode : '-'} ({mac || '-'})
                          </span>
                        </div>
                      </div>
                    )}

                    <BleBeaconMap
                      asset={assetData}
                      latestRecord={latestRecord}
                      historyData={historyData}
                      showHistory={showHistory}
                      detectorAsset={detectorAsset}
                      detectorAssetsMap={detectorAssetsMap}
                      selectedHistoryRecord={selectedHistoryRecord}
                      isMaximized={mapMaximized}
                      onToggleMaximize={() => setMapMaximized(v => !v)}
                    />

                    {mapMaximized && (
                      <>
                        {!infoPanelExpanded && (
                          <div className="device-info-collapsed-strip">
                            <button
                              className="device-info-collapsed-expand"
                              onClick={() => setInfoPanelExpanded(true)}
                              title="Expand info"
                            >
                              <Icon type="left" />
                            </button>
                            <button className="device-info-collapsed-tab-label" onClick={() => setInfoPanelExpanded(true)}>
                              Beacon Status
                            </button>
                            <button className="device-info-collapsed-tab-label" onClick={() => setInfoPanelExpanded(true)}>
                              Fleet History
                            </button>
                          </div>
                        )}

                        <div
                          className="device-info-glass"
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            width: infoPanelExpanded ? 350 : 0,
                            height: '100%',
                            zIndex: 1000,
                            overflow: 'hidden',
                            transition: 'width 0.25s ease',
                            background: 'rgba(255,255,255,0.88)',
                            backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            borderLeft: '1px solid rgba(200,200,200,0.6)',
                            boxShadow: '-2px 0 24px rgba(0,0,0,0.18)',
                          }}
                        >
                          <button
                            className="device-info-collapse-btn"
                            onClick={() => setInfoPanelExpanded(false)}
                            title="Collapse panel"
                          >
                            <Icon type="right" />
                          </button>
                          <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
                            <BleBeaconInfo
                              latestRecord={latestRecord}
                              detectorAsset={detectorAsset}
                              detectorAssetsMap={detectorAssetsMap}
                              historyData={historyData}
                              selectedHistoryRecord={selectedHistoryRecord}
                              onSelectRecord={setSelectedHistoryRecord}
                              loading={beaconLoading}
                              onRefreshLatest={refreshLatest}
                              onFleetHistory={(range) => fetchBeaconData(range[0], range[1], true)}
                              onClearHistory={() => {
                                setHistoryData([])
                                setShowHistory(false)
                                setDetectorAssetsMap({})
                                setSelectedHistoryRecord(null)
                              }}
                              isLiveFeedActive={isLiveFeedActive}
                              onToggleLiveFeed={setIsLiveFeedActive}
                              liveTowingRecord={liveTowingRecord}
                              lastTowDeviceAsset={lastTowDeviceAsset}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Col>

              {!mapMaximized && (
                <Col
                  sm={24} xs={24}
                  style={{ flex: '0 0 33.333333%', maxWidth: '33.333333%', alignSelf: 'stretch' }}
                >
                  <BleBeaconInfo
                    latestRecord={latestRecord}
                    detectorAsset={detectorAsset}
                    detectorAssetsMap={detectorAssetsMap}
                    historyData={historyData}
                    selectedHistoryRecord={selectedHistoryRecord}
                    onSelectRecord={setSelectedHistoryRecord}
                    loading={beaconLoading}
                    onRefreshLatest={refreshLatest}
                    onFleetHistory={(range) => fetchBeaconData(range[0], range[1], true)}
                    onClearHistory={() => {
                      setHistoryData([])
                      setShowHistory(false)
                      setDetectorAssetsMap({})
                      setSelectedHistoryRecord(null)
                    }}
                    isLiveFeedActive={isLiveFeedActive}
                    onToggleLiveFeed={setIsLiveFeedActive}
                    liveTowingRecord={liveTowingRecord}
                  />
                </Col>
              )}
            </Row>
          </>
        )}
      </Spin>
    </Auxiliary>
  )
}

export default connect(mapStateToProps, mapDispatchToProps())(BleBeaconDetails)
